import { ReactNode } from 'react'
import { Text } from '@/components'
import { AlertMessage } from '@/app/components'

export type BillOnFileNoticeProps = {
  /** Presigned URL of the bill on file; when set, renders a "view bill" link. */
  billUrl?: string | null
  /** Reveals the upload card again so the user can upload a different bill. */
  onUploadAnother: () => void
  /** Trailing sentence after "…already on file." Defaults to account-details copy. */
  detail?: ReactNode
}

/**
 * Success banner shown when a utility bill has already been uploaded — so we
 * don't re-prompt for one. Offers a link to view the bill and an "Upload another
 * bill" action that re-reveals the uploader. Shared by the utility selection
 * page and the account-details step so the messaging stays identical.
 */
export default function BillOnFileNotice({
  billUrl,
  onUploadAnother,
  detail,
}: BillOnFileNoticeProps) {
  return (
    <AlertMessage variant="success">
      <Text variant="body2" className="text-initial">
        Your utility bill is already on file
        {billUrl ? (
          <>
            {' ('}
            <a
              href={billUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="!underline cursor-pointer font-bold"
            >
              view bill
            </a>
            {')'}
          </>
        ) : null}
        . {detail ?? 'You can review or update your account details below.'}
      </Text>
      <Text
        variant="link"
        className="mt-2 inline-block"
        onClick={onUploadAnother}
      >
        Upload another bill
      </Text>
    </AlertMessage>
  )
}
